// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).
// Extraction preview for "Start from a document" (create-doc-interactive, design "Shared library"). Pure functions
// shared by the web app and the API: which fields a preview uses, the Preview_Schema built from them, the identity
// used for "out of date", and how a result is laid out against the schema with "Not found" rows.
import { formToSchema } from './schema/convert.js';
import { resultSegments, } from './types.js';
/** Placeholder metadata of every Preview_Schema (requirements glossary, Req 5.3). */
export const PREVIEW_META = {
    /** Passes BLUEPRINT_NAME_PATTERN; used only for validation, never shown. */
    name: 'Blueprint preview',
    /** Becomes `class`. */
    documentType: 'Document',
    description: 'Fields to extract from this document.',
};
/** Blueprint name of a Preview_Run in history (Req 6.4). */
export const PREVIEW_BLUEPRINT_NAME = '(blueprint preview)';
/** Blueprint name of a Set_Preview_Run in history (blueprint-sets Req 11.4). */
export const SET_PREVIEW_BLUEPRINT_NAME = '(set preview)';
/** Shown with every Extraction_Preview (Req 3.2). */
export const PREVIEW_LABEL = 'Preview: blueprint not saved';
/** The selection changed after the shown preview was requested (Req 3.5). */
export const PREVIEW_STALE_SELECTION = 'Out of date: the selection changed';
/** The Editor's fields changed after the shown preview was requested (Req 4.7). */
export const PREVIEW_STALE_FIELDS = 'Out of date: the fields changed';
/** The Sample_Document is past retention (Req 2.6). */
export const SAMPLE_EXPIRED_TEXT = 'The sample document has expired. Upload it again to preview.';
/** Any schema (form or JSON view) with its metadata replaced by the placeholders. Idempotent. */
export function toPreviewSchema(schema) {
    return { ...schema, class: PREVIEW_META.documentType, description: PREVIEW_META.description };
}
/** Preview_Schema of a list of fields: formToSchema with placeholder metadata. */
export function previewSchemaOfFields(fields) {
    return formToSchema({ ...PREVIEW_META, fields });
}
/**
 * The fields a modal preview uses (Req 1.6) and that "Add N selected" adds: the Editor's fields, then each selected
 * suggestion whose name (case-insensitive) is not already in the Editor. The Editor's definition wins on a name match.
 * Same rule as the editor reducer's `acceptSuggestions`.
 */
export function mergeForPreview(editorFields, selected) {
    const taken = new Set(editorFields.map((f) => f.name.toLowerCase()));
    return [...editorFields, ...selected.filter((f) => !taken.has(f.name.toLowerCase()))];
}
/** Selected suggestions in list order; a selected group or table keeps every child the model proposed (Req 1.3). */
export function selectedSuggestions(suggestions, selected) {
    return suggestions.filter((_, i) => selected.has(i));
}
/** Canonical identity of a selection, for "out of date" (Req 3.5). Independent of the order of selection. */
export const selectionKey = (selected) => [...selected].sort((a, b) => a - b).join(',');
/** JSON with sorted keys (undefined values dropped), so the result does not depend on object key order. */
export function canonicalJson(v) {
    if (Array.isArray(v))
        return `[${v.map(canonicalJson).join(',')}]`;
    if (v && typeof v === 'object') {
        const o = v;
        return `{${Object.keys(o)
            .sort()
            .filter((k) => o[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`)
            .join(',')}}`;
    }
    return JSON.stringify(v);
}
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function definitionOf(schema, ref) {
    if (typeof ref !== 'string')
        return null;
    const m = /^#\/definitions\/([^/]+)$/.exec(ref);
    const def = m && isRecord(schema.definitions) ? schema.definitions[m[1]] : undefined;
    return isRecord(def) ? def : null;
}
const childNames = (def) => def && isRecord(def.properties) ? Object.keys(def.properties) : [];
/**
 * Layout of a schema, read directly from `properties`, `$ref`, and `items.$ref` (not through `schemaToForm`), so it
 * also works for JSON-view schemas the form cannot represent (Req 4.5). Paths match `normalizeCustomOutput`.
 */
export function previewLayout(schema) {
    const props = isRecord(schema.properties) ? schema.properties : {};
    return Object.entries(props).map(([name, p]) => {
        if (isRecord(p) && p.$ref !== undefined) {
            return {
                kind: 'group',
                path: name,
                children: childNames(definitionOf(schema, p.$ref)).map((c) => `${name}.${c}`),
            };
        }
        if (isRecord(p) && p.type === 'array' && isRecord(p.items) && p.items.$ref !== undefined) {
            return { kind: 'table', path: name, children: childNames(definitionOf(schema, p.items.$ref)) };
        }
        return { kind: 'field', path: name };
    });
}
/** "Not found" test for one value (Req 3.3): absent, null, "", or an empty list. */
export const isNotFound = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
function fieldRow(path, seg) {
    const f = seg?.fields.find((x) => x.path === path);
    if (!f || isNotFound(f.value))
        return { kind: 'field', path, found: false };
    return { kind: 'field', path, found: true, value: f.value, confidence: f.confidence };
}
function rowsOf(layout, seg) {
    return layout.map((node) => {
        if (node.kind === 'group') {
            const children = (node.children ?? []).map((c) => fieldRow(c, seg));
            return { kind: 'group', path: node.path, found: children.some((c) => c.found), children };
        }
        if (node.kind === 'table') {
            const table = seg?.tables.find((t) => t.name === node.path);
            return table && table.rows.length > 0
                ? { kind: 'table', path: node.path, found: true, table }
                : { kind: 'table', path: node.path, found: false };
        }
        return fieldRow(node.path, seg);
    });
}
/**
 * Joins a schema's layout with a normalized result, per segment: one row per layout node in schema order. A segment
 * whose status is not `MATCH` was not processed with the blueprint, so all its rows are "Not found".
 */
export function previewView(schema, result) {
    const layout = previewLayout(schema);
    const segments = resultSegments(result).map((segment) => ({
        segment,
        rows: rowsOf(layout, segment.status === 'MATCH' ? segment : null),
    }));
    return { segments, noMatch: !segments.some((s) => s.segment.status === 'MATCH') };
}
//# sourceMappingURL=preview.js.map